import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";

import { api, AssetLocation, AssetLocationTypes, Booking, ErrorToString, IKeyManagerBarcodeScanKeyMovementResult, KeyManagementTypes, KeyMangerBarcodeResultType, KeyMovementInfo, KeyMovementStatus } from "../../../../logic/api";

import flex from "../../../../styles/flex";

import BookingListItem from "../../../../components/lists/listItems/BookingListItem/BookingListItem";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import LocationPickerDialog from "../../../../components/pickers/locationPicker/LocationPickerDialog";
import BarcodeScanner from "../../../../components/thirdParty/react-barcode-reader";

import { SessionContext } from "../../../../utility/useSession";
import usePaths from "../../../../utility/paths";
import { useDate } from "../../../../utility/useDate";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "../../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { useSoundEffects, SoundType } from "../../../../utility/utils";
import { FiArrowLeft, FiKey } from "react-icons/fi";
import Icon from "../../../../components/editable/EditableField/components/Icon";
import Flex from "../../../../styles/flex";
import { usePreferences } from "../../../../utility/usePreferences";

interface KeyMovementError {
  detail?: string,
  error?: string
}

const KeyMovementsScanner: React.FunctionComponent<{
  show: boolean;
  onClose: () => void;
}> = (props) => {
  const paths = usePaths();
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const [scanned, setScanned] = useState<Booking[]>([]);
  const [barcode, setBarcode] = useState("");
  const [err, setErr] = useState<KeyMovementError>({});
  const [warn, setWarn] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>();
  const fetchScanData = api.hooks.keyManager.barcodeScanKeyMovement();
  const [location, setLocation] = useState<AssetLocation | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const { localizeDate } = useDate();

  const isKeyThenLocationScanningReleased = useReleaseManagement(
    ReleaseFlag.releaseKeyThenLocationScanning
  );
  const isPreventKeyMovementToDifferentCarParkReleased = useReleaseManagement(
    ReleaseFlag.releasePreventKeyMovementToDifferentCarPark
  );
  const isMoveKeyVehicleModalIconsReleased = useReleaseManagement(
    ReleaseFlag.releaseMoveKeyVehicleModalIcons
  );

  const translationKey = fetchScanData.data?.keyMovementInfo ? `keyMovementInfo.${KeyMovementInfo[fetchScanData.data.keyMovementInfo]}` : '';
  const infoTranslation = t(translationKey);
  const infoString = infoTranslation ? `(${infoTranslation})` : '';
  const isLocationCapacityReached = (loc: AssetLocation) => loc.capacity > 0 && loc.alertIfOverfilled && loc.keyCount >= loc.capacity;
  const playSound = useSoundEffects();


  const { keymovements_key_then_location } = usePreferences();
  const expectKeyScanBeforeLocation = isKeyThenLocationScanningReleased && keymovements_key_then_location;
  const [lastScannedKeyBarcode, setLastScannedKeyBarcode] = useState("");
  const waitingForKeyScan = expectKeyScanBeforeLocation == true && lastScannedKeyBarcode.length == 0;
  const [showLaneFullWarning, setShowLaneFullWarning] = useState(false);

  useEffect(() => {
    if (props.show == true) {
      setLocation(null);
      setErr({});
      setWarn(null);
      setMsg(null);
      setScanned([]);
      setBarcode("");
      setLastScannedKeyBarcode("");
      setShowLaneFullWarning(false);
    }
  }, [props.show]);

  const clearDisplay = () => {
    setErr({});
    setWarn(null);
    setMsg(null);
  }

  const switchLocation = (loc: AssetLocation | null) => {

    setShowLocationPicker(false);
    if (loc == null) {
      return;
    }

    if (location != null) {
      if (location.id == loc.id) {
        setMsg(null);
        setErr({ error: t("errors.locationAlreadySet", { location: loc.name }) });
        playSound(SoundType.Bad);
        return;
      }
    }

    if (isLocationCapacityReached(loc)) {
      setWarn(t("errors.locationFull"))
      playSound(SoundType.Warning)
    }
    else {
      setWarn(null)
      playSound(SoundType.Swap);
    }

    setErr({});
    setMsg(t("messages.locationChanged", { location: loc.name }));
    setLocation(loc);
    setScanned([]);
  };

  const onBarcodeScanned = async (scannedBarcode: string) => {
    if (scanBusy == true) {
      playSound(SoundType.Busy);
      return;
    }
    setScanBusy(true);
    await processScanned(scannedBarcode, location, scanned);
    setScanBusy(false);
  }

  const handleError = (result: IKeyManagerBarcodeScanKeyMovementResult) => {
    if (result.err === "errors.vehicleInReturnsBeforeKey") {
      setErr({ error: t(result.err, { location: result.location?.name }) })
    } else if (isPreventKeyMovementToDifferentCarParkReleased && result.err === "errors.cannotMoveKeyToDifferentCarPark") {
      setErr({ error: t(result.err, { location: result.location?.name, carPark: result.carPark }) })
    } else if (result.err) {
      setErr({ error: t(result.err) });
    }
    playSound(SoundType.Bad);
  }

  const handleKeyScan_KeyThenLocation = async (barcode: string, result: IKeyManagerBarcodeScanKeyMovementResult) => {
    setShowLaneFullWarning(false);

    if (result.type == KeyMangerBarcodeResultType.err) {
      handleError(result);
      return;
    }
    if (waitingForKeyScan) {
      if (result.type != KeyMangerBarcodeResultType.booking) {
        setErr({ error: t("errors.scanKeyFirst") });
        playSound(SoundType.Bad);
        return;
      }

      if (result.booking == null) {
        setErr({ error: t("errors.keyBarcodeNotFound") });
        playSound(SoundType.Bad);
        return;
      }


      setScanned([...[result.booking]]);
      setLastScannedKeyBarcode(barcode);
      playSound(SoundType.Good);
      setMsg(t("messages.keyScanned", { ref: result.booking.ref }));
      return;
    } else {
      if (result.type != KeyMangerBarcodeResultType.location) {
        setErr({ error: t("errors.scanLocationForMovement") });
        playSound(SoundType.Bad);
        return;
      }

      if (result.location == null) {
        setErr({ error: t("errors.locationNotFound") });
        playSound(SoundType.Bad);
        return;
      }

      await didScanLocationBarcode(result.location);
    }
  };

  const didScanLocationBarcode = async (location: AssetLocation | null) => {
    if (location == null) {
      setShowLaneFullWarning(false);
      return;
    }
    setShowLaneFullWarning(isLocationCapacityReached(location) == true);
    await processScanned(lastScannedKeyBarcode, location, []);
    setLastScannedKeyBarcode("");
  }

  const handleBarcodeScanResult = (result: IKeyManagerBarcodeScanKeyMovementResult, location: AssetLocation | null, scanned: Booking[]) => {
    return new Promise<boolean>((accept) => {
      if (result.type == KeyMangerBarcodeResultType.booking) {
        if (location == null) {
          playSound(SoundType.Bad)
          setErr({ error: t("errors.scanLocation") });
          accept(true);
          return;
        }
        if (result.booking == null) {
          setErr({ error: t("errors.keyBarcodeNotFound") });
          playSound(SoundType.Bad)
          accept(true);
          return;
        }

        const exists = scanned.find((b) => {
          if (result.booking == null) {
            return false;
          }
          return result.booking.id == b.id;
        });

        if (exists != null) {
          setErr({ error: t("errors.keyAlreadyScanned") });
          playSound(SoundType.Bad)
          accept(true);
          return;
        }

        if (result.keyMovementInfo) {
          if (result.keyMovementStatus === KeyMovementStatus.allow) {
            setScanned([...[result.booking], ...scanned]);
            playSound(SoundType.Good);
            setMsg(t("messages.keyMoved", { ref: result.booking.ref, location: location.name }));
            accept(true);
            return;
          }


          if (result.keyMovementStatus === KeyMovementStatus.alert) {
            setScanned([...[result.booking], ...scanned]);
            playSound(SoundType.Warning);
            setWarn(t("messages.keyMoved", { ref: result.booking.ref, location: location.name }));
            accept(true);
            return;
          }

          if (result.keyMovementStatus === KeyMovementStatus.block) {
            playSound(SoundType.Bad)

            if (result.keyMovementInfo === KeyMovementInfo.wrongDate) {
              setErr({ error: t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }), detail: `${t("errors.keyMovementBlockedDate").toUpperCase()}` });
            }

            if (result.keyMovementInfo === KeyMovementInfo.wrongTimeSlot) {
              setErr({ error: t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }), detail: `${t("errors.keyMovementBlockedDateTimeslot").toUpperCase()}` });
            }

            if (result.keyMovementInfo === KeyMovementInfo.differentAssignedLane) {
              setErr({ error: t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }), detail: `${t("errors.keyMovementBlockedAllocation").toUpperCase()}` });
            }

            if (result.keyMovementInfo === KeyMovementInfo.noAssignedLane) {
              setErr({ error: t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }), detail: `${t("errors.keyMovementBlockedAllocation").toUpperCase()}` });
            }
            if (result.keyMovementInfo === KeyMovementInfo.zoneNotAllowed) {
              setErr({ error: t("errors.keyMovementBlockedLocation", { ref: result.booking.ref, location: location.name }), detail: `${t("errors.keyMovementBlockedZone").toUpperCase()}` });
            }


            accept(true);
            return;
          }
        }

        if (result.keyMoved == true && result.vehicleMoved == true) {
          setMsg(t("messages.keyAndVehicleMoved", { ref: result.booking.ref, location: location.name }));
          setScanned([...[result.booking], ...scanned]);
          playSound(SoundType.Good);
        } else if (result.keyMoved == true) {
          setMsg(t("messages.keyMoved", { ref: result.booking.ref, location: location.name }));
          setScanned([...[result.booking], ...scanned]);
          playSound(SoundType.Good);
        } else if (result.vehicleMoved == true) {
          setMsg(t("messages.vehicleMoved", { ref: result.booking.ref, location: location.name }));
          setScanned([...[result.booking], ...scanned]);
          playSound(SoundType.Good);
        }
      } else if (result.type == KeyMangerBarcodeResultType.location) {
        if (result.location) {
          switchLocation(result.location);
        } else {
          setErr({ error: t("errors.noLocationReturned") });
          playSound(SoundType.Bad);
        }
      } else if (result.type == KeyMangerBarcodeResultType.err) {
        handleError(result);
      }
      accept(true);
    });
  }

  const processScanned = async (scannedBarcode: string, location: AssetLocation | null, scanned: Booking[]) => {
    clearDisplay();
    setShowLocationPicker(false);

    return new Promise<boolean>((accept) => {
      setErr({});
      setMsg(null);

      const trimmedBarcode = (scannedBarcode ?? "").trim();
      if (trimmedBarcode.length == 0) {
        accept(true);
        return;
      }
      setBarcode("");
      fetchScanData.send(session.current.context, {
        scan: trimmedBarcode,
        locationId: location == null ? null : location.id,
        scanDate: localizeDate(new Date()).toISO() ?? "",
        lookupOnly: waitingForKeyScan,
      }, async (result) => {
        if (result == null) {
          playSound(SoundType.Bad)
          accept(true);
          return;
        }

        if (expectKeyScanBeforeLocation && location == null) {
          await handleKeyScan_KeyThenLocation(scannedBarcode, result);
          accept(true);
          return;
        }

        await handleBarcodeScanResult(result, location, scanned);
        accept(true);
      });
    })
  }

  const keySelectionTypes = [AssetLocationTypes.Lane, AssetLocationTypes.Zone, AssetLocationTypes.User, AssetLocationTypes.DynamicBox, AssetLocationTypes.Hook, AssetLocationTypes.KeyBox];

  return <Fragment>

    <LocationPickerDialog
      title={t("keys.labels.selectLocation")}
      show={showLocationPicker}

      displayTypes={[AssetLocationTypes.Zone, AssetLocationTypes.Lane, AssetLocationTypes.Users, AssetLocationTypes.User, AssetLocationTypes.Keyroom, AssetLocationTypes.Hook, AssetLocationTypes.KeyBox, AssetLocationTypes.DynamicBox]}
      selectionTypes={keySelectionTypes}
      keyManagementTypes={[KeyManagementTypes.box, KeyManagementTypes.hook]}
      onChange={(loc) => {
        if (expectKeyScanBeforeLocation == true) return didScanLocationBarcode(loc);
        switchLocation(loc)
      }}
      onCancel={() => {
        setShowLocationPicker(false);
      }}
    >
    </LocationPickerDialog>

    <Dialog
      maxWidth={"md"}
      open={props.show}
    >
      <DialogTitle
        style={{
          color: "gray",
          textAlign: "center",
          paddingBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column"
        }}
      >
        {isMoveKeyVehicleModalIconsReleased ? (<FiKey size={100} />) : null}
        <Typography variant="h6" component="h2">{t("keys.labels.keyMovements")}</Typography>
      </DialogTitle>

      <DialogTitle style={{ textAlign: "left", paddingBottom: 10 }}>
        {!expectKeyScanBeforeLocation && (
          (location == null) ? (
            <Fragment><span style={{ color: "red" }}>{t("keys.labels.scanLocation")}</span></Fragment>
          ) : (
            <Fragment><b style={{ color: "red" }}>{location.name}</b></Fragment>
          )
        )}

        {expectKeyScanBeforeLocation && (
          (waitingForKeyScan) ? (
            <Fragment>
              <div style={{ padding: "20px", border: "solid 2px blue", backgroundColor: "#EEE", borderRadius: 7, marginBottom: 20 }}>
                <span style={{ color: "blue" }}>{t("keys.labels.waitingForKeyScan")}</span>
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div style={{ padding: "20px", border: "solid 2px red", backgroundColor: "#EEE", borderRadius: 7, marginBottom: 20 }}>
                <div style={flex.flexRows}>
                  <div style={Flex.flexAuto}>
                    <Icon label="Scan" onClick={() => {
                      setShowLaneFullWarning(false);
                      setLastScannedKeyBarcode("");
                      setScanned([]);
                    }}>
                      <FiArrowLeft color="red" />
                    </Icon>
                  </div>
                  <div style={Flex.flexFill}>
                    <div style={{ padding: 10 }}>
                      <b style={{ color: "red" }}>{t("keys.labels.waitingForLocationScan", { barcode: lastScannedKeyBarcode })}</b>
                    </div>
                  </div>
                </div>
              </div>
            </Fragment>
          )
        )}
      </DialogTitle>

      {location != null && (
        <DialogTitle style={{ margin: 0, padding: 0, paddingLeft: 20, paddingRight: 20, paddingBottom: 10, textAlign: "center" }}>
          {t("keys.labels.keysScanned", { length: scanned.length })}
        </DialogTitle>
      )}

      <DialogContent style={{ minHeight: 50 }}>
        <div style={flex.flexRows}>
          <div style={flex.flexFill}>
            {scanBusy == false && (
              <BarcodeScanner onError={(err: string) => {
                console.log(err);
              }} onScan={(scan: string) => {
                onBarcodeScanned(scan);
              }} minLength={1} />
            )}

            <TextField
              focused={true}
              inputProps={{
                autoComplete: 'off',
                name: "input",
              }}
              style={{
                width: "100%",
              }}

              inputRef={inputRef}
              type={"search"}
              size="small"
              label={t("expressCheckout.labels.barcode")}
              variant="outlined"
              value={barcode}
              onChange={(e) => {
                setBarcode(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key == "Enter") {
                  onBarcodeScanned(barcode);
                }
              }}
            />
          </div>

          {(expectKeyScanBeforeLocation == false || lastScannedKeyBarcode.length > 0) && (
            <div style={flex.flexAuto}>
              <div style={{ marginLeft: 10 }}>
                <Button onClick={() => {
                  setShowLocationPicker(true);
                }} variant="contained" color={location == null ? "secondary" : "primary"}>
                  {location == null ? (<Fragment>{t("keys.labels.selectLocation")}</Fragment>) : (<Fragment>{t("keys.labels.changeLocation")}</Fragment>)}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      <DialogContent>
        <ProgressDialog status={fetchScanData.status} />

        <div style={{ width: 800 }}>
          {msg != null && (
            <div data-testid="msg-ok" style={{ padding: 20, marginBottom: 10, backgroundColor: "lightgreen", textAlign: "center" }}>
              <b>{msg.toUpperCase()}</b>
            </div>
          )}

          {warn && (
            <div data-testid="msg-warn" style={{ padding: 20, marginBottom: 10, backgroundColor: "#EFCB6D", textAlign: "center" }}>
              <b>{warn.toUpperCase()} {infoString.toUpperCase()}</b>
            </div>
          )}

          {showLaneFullWarning && (
            <div data-testid="msg-ok" style={{ padding: 20, marginBottom: 10, backgroundColor: "#EFCB6D", textAlign: "center" }}>
              <b>{t("errors.locationFull").toUpperCase()}</b>
            </div>
          )}

          {err?.error != null && (
            <div data-testid="msg-block" style={{ padding: 20, marginBottom: 10, backgroundColor: "pink", textAlign: "center" }}>
              <b>{err.error.toUpperCase()}</b>
              {err?.detail &&
                <>
                  <br />
                  <br />
                  <b>{err.detail.toUpperCase()}</b>
                </>
              }
            </div>
          )}

          {fetchScanData.err != null && (
            <div data-testid="msg-error" style={{ padding: 20, marginBottom: 10, backgroundColor: "pink", textAlign: "center" }}>
              <b>{ErrorToString(fetchScanData.err)}</b>
            </div>
          )}

          <div style={{ minHeight: 400 }}>
            {
              scanned.map((booking, key) => {
                return <BookingListItem booking={booking} key={key} onClick={() => {
                  window.open(paths.booking(booking.id));
                }}></BookingListItem>
              })
            }
          </div>
        </div>
      </DialogContent>

      <DialogActions>

        <Button onClick={() => {
          props.onClose();
        }}>
          <b>{t("general.done")}</b>
        </Button>
      </DialogActions>
    </Dialog>


  </Fragment>
}


export default KeyMovementsScanner;
